/* ==========================================================================
   MIRROR · Puente de salida del navegador de TikTok / Instagram / Facebook
   --------------------------------------------------------------------------
   No hay un truco único que funcione siempre (cada app y versión bloquea
   distinto), así que el puente trabaja en capas y nunca deja un callejón:

   1. Intento directo, en el mismo toque del usuario:
      · Android → intent:// al paquete de WhatsApp (abre el chat, no Chrome)
      · iOS     → esquema whatsapp:// (abre el chat)
   2. Si en ~1,3 s la página sigue visible (la app lo bloqueó):
      · Se guarda la intención en la URL (?go=…) ANTES de pedir nada.
      · Hoja guiada con flecha al botón ••• → «Abrir en navegador».
      · Botón que intenta abrir Safari/Chrome con esa URL (x-safari / intent).
   3. Al reabrir en el navegador real con ?go=…, la página muestra
      «Continuar a WhatsApp»: un solo toque, sin volver a buscar nada.
   4. Siempre visible: número para copiar.

   Eventos: window 'mirror:bridge' + Shopify.analytics.publish('mirror_bridge').
   API: window.MirrorBridge
   ========================================================================== */
(function () {
  'use strict';
  if (window.MirrorBridge) return;

  var UA = navigator.userAgent || '';
  var IS_IOS = /iPhone|iPad|iPod/i.test(UA) || (/Macintosh/i.test(UA) && navigator.maxTouchPoints > 1);
  var IS_ANDROID = /Android/i.test(UA);
  var APP = (function () {
    if (/BytedanceWebview|musical_ly|TikTok|trill_|aweme/i.test(UA)) return 'tiktok';
    if (/Instagram/i.test(UA)) return 'instagram';
    if (/FBAN|FBAV|FB_IAB|FBIOS|FB4A/i.test(UA)) return 'facebook';
    if (/Messenger/i.test(UA)) return 'messenger';
    if (/Snapchat/i.test(UA)) return 'snapchat';
    if (/Line\//i.test(UA)) return 'line';
    if (IS_ANDROID && /; wv\)/.test(UA)) return 'webview';
    return '';
  })();
  var IN_APP = APP !== '';
  var WAIT_MS = 1300;
  var LOG_KEY = 'mirror_bridge_log';

  /* ---------- utilidades ---------- */
  function track(name, data) {
    var payload = Object.assign({ event: name, app: APP || 'browser', os: IS_IOS ? 'ios' : IS_ANDROID ? 'android' : 'other', t: Date.now() }, data || {});
    try {
      var log = JSON.parse(localStorage.getItem(LOG_KEY) || '[]');
      log.push(payload);
      localStorage.setItem(LOG_KEY, JSON.stringify(log.slice(-40)));
    } catch (e) { /* sin almacenamiento */ }
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('mirror_bridge', payload);
      }
    } catch (e) { /* sin analytics */ }
    try { window.dispatchEvent(new CustomEvent('mirror:bridge', { detail: payload })); } catch (e) {}
  }

  function inviteCode(url) {
    var m = String(url || '').match(/chat\.whatsapp\.com\/(?:invite\/)?([A-Za-z0-9]+)/);
    return m ? m[1] : '';
  }

  function waWebUrl(phone, text) {
    return 'https://wa.me/' + phone + (text ? '?text=' + encodeURIComponent(text) : '');
  }

  /* Destino directo a la app de WhatsApp según SO */
  function directUrl(action) {
    if (action.type === 'wa') {
      var q = 'phone=' + action.phone + (action.text ? '&text=' + encodeURIComponent(action.text) : '');
      if (IS_ANDROID) {
        return 'intent://send/?' + q + '#Intent;scheme=whatsapp;package=com.whatsapp;S.browser_fallback_url=' +
          encodeURIComponent(waWebUrl(action.phone, action.text)) + ';end';
      }
      if (IS_IOS) return 'whatsapp://send?' + q;
      return waWebUrl(action.phone, action.text);
    }
    if (action.type === 'community') {
      var code = inviteCode(action.url);
      if (code && IS_ANDROID) {
        return 'intent://chat.whatsapp.com/' + code + '#Intent;scheme=https;package=com.whatsapp;S.browser_fallback_url=' +
          encodeURIComponent(action.url) + ';end';
      }
      if (code && IS_IOS) return 'whatsapp://chat?code=' + code;
      return action.url;
    }
    return action.url;
  }

  /* Abrir una URL https en el navegador del sistema */
  function browserUrl(url) {
    if (IS_IOS) return url.replace(/^https?:\/\//, 'x-safari-https://');
    if (IS_ANDROID) {
      var u = new URL(url);
      return 'intent://' + u.host + u.pathname + u.search + '#Intent;scheme=https;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;S.browser_fallback_url=' +
        encodeURIComponent(url) + ';end';
    }
    return url;
  }

  /* Navega y averigua si la página se fue (éxito) o siguió visible (bloqueado) */
  function attempt(url, label) {
    return new Promise(function (resolve) {
      var left = false;
      var done = false;
      function onHide() { if (document.visibilityState === 'hidden') left = true; }
      function onBlur() { left = true; }
      function finish() {
        if (done) return;
        done = true;
        document.removeEventListener('visibilitychange', onHide);
        window.removeEventListener('pagehide', onBlur);
        window.removeEventListener('blur', onBlur);
        resolve(left);
      }
      document.addEventListener('visibilitychange', onHide);
      window.addEventListener('pagehide', onBlur);
      window.addEventListener('blur', onBlur);
      try {
        window.location.href = url;
      } catch (e) {
        track('attempt_error', { label: label, message: String(e && e.message) });
      }
      setTimeout(finish, WAIT_MS);
    });
  }

  /* Guarda la intención en la URL actual: si el usuario usa ••• → Abrir en navegador,
     el navegador abre exactamente esta URL y la página continúa sola. */
  function persistIntent(go) {
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('go', go);
      if (APP) u.searchParams.set('from', APP);
      try {
        if (window.Shopify && Shopify.theme && Shopify.theme.role && Shopify.theme.role !== 'main' && Shopify.theme.id) {
          u.searchParams.set('preview_theme_id', String(Shopify.theme.id));
        }
      } catch (e) {}
      history.replaceState(history.state, '', u.toString());
      return u.toString();
    } catch (e) {
      return window.location.href;
    }
  }

  function clearIntent() {
    try {
      var u = new URL(window.location.href);
      u.searchParams.delete('go');
      u.searchParams.delete('from');
      history.replaceState(history.state, '', u.toString());
    } catch (e) {}
  }

  function readIntent() {
    try {
      var u = new URL(window.location.href);
      return { go: u.searchParams.get('go'), from: u.searchParams.get('from') };
    } catch (e) {
      return { go: null, from: null };
    }
  }

  function copy(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text).then(function () { return true; }, function () { return fallbackCopy(text); });
    }
    return Promise.resolve(fallbackCopy(text));
  }
  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:-100px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      var ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch (e) {
      return false;
    }
  }

  /* Flujo completo para un toque en un enlace de WhatsApp/comunidad */
  /* mode: 'guide' → muestra la guía al instante (sin intento automático; evita el aviso
           «No se puede completar la acción» que iOS/TikTok muestra al bloquear un esquema).
     mode: 'auto'  → en Android intenta abrir WhatsApp directo; en iPhone siempre guía. */
  function open(action, hooks, mode) {
    hooks = hooks || {};
    mode = mode || 'guide';
    track('tap', { type: action.type, go: action.go, label: action.label, mode: mode });

    if (!IN_APP) {
      // Navegador real: WhatsApp abre normal; nada que hacer
      track('direct_browser', { type: action.type, go: action.go });
      return Promise.resolve(true);
    }

    var continueUrl = persistIntent(action.go);
    if (mode !== 'auto' || !IS_ANDROID) {
      track('guide_shown', { type: action.type, go: action.go });
      if (hooks.onBlocked) hooks.onBlocked(continueUrl);
      return Promise.resolve(false);
    }
    var url = directUrl(action);
    return attempt(url, 'direct').then(function (ok) {
      if (ok) {
        track('direct_ok', { type: action.type, go: action.go });
        clearIntent();
        return true;
      }
      track('direct_blocked', { type: action.type, go: action.go });
      if (hooks.onBlocked) hooks.onBlocked(continueUrl);
      return false;
    });
  }

  /* Segundo intento (otro toque del usuario): abrir la página en Safari/Chrome */
  function openInBrowser(url) {
    track('browser_try', {});
    return attempt(browserUrl(url), 'browser').then(function (ok) {
      track(ok ? 'browser_ok' : 'browser_blocked', {});
      return ok;
    });
  }

  window.MirrorBridge = {
    app: APP,
    inApp: IN_APP,
    isIOS: IS_IOS,
    isAndroid: IS_ANDROID,
    open: open,
    openInBrowser: openInBrowser,
    persistIntent: persistIntent,
    directUrl: directUrl,
    browserUrl: browserUrl,
    waWebUrl: waWebUrl,
    readIntent: readIntent,
    clearIntent: clearIntent,
    copy: copy,
    track: track,
    log: function () {
      try { return JSON.parse(localStorage.getItem(LOG_KEY) || '[]'); } catch (e) { return []; }
    }
  };

  if (IN_APP) document.documentElement.classList.add('is-in-app', 'is-in-app--' + APP);
})();
